import { copy } from '@ohear/shared'
import { Badge, Card, EmptyState, Spinner } from '@ohear/ui'
import { Navigate, createFileRoute } from '@tanstack/react-router'
import { AccountTabs } from '../../features/account/AccountTabs'
import { ExportButtons, type ExportItem } from '../../features/exports'
import { useAccountProfile } from '../../lib/api/account'
import { useEarnings } from '../../lib/api/earnings'
import type { EarningsBatchRow } from '../../lib/api/earnings'
import { useT } from '../../lib/i18n/use-t'
import { AccountHeader } from './profile'

export const Route = createFileRoute('/account/earnings')({
  component: EarningsPage,
})

function EarningsPage() {
  const t = useT()
  const profile = useAccountProfile()
  const earnings = useEarnings()

  if (profile.isLoading) {
    return (
      <Section>
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading earnings...
        </div>
      </Section>
    )
  }

  if (!profile.data) {
    return (
      <Section>
        <Card>
          <p className="m-0">{t(copy.common.SIGN_IN_REQUIRED.earnings)}</p>
        </Card>
      </Section>
    )
  }

  if (profile.data.primary_role !== 'actor') {
    return <Navigate to="/account/profile" replace />
  }

  const isActor = true
  const summaries = earnings.data?.summary ?? []
  const projects = earnings.data?.projects ?? []
  const forms1099 = earnings.data?.forms_1099 ?? []
  const currentYear = new Date().getFullYear()
  const cur = summaries.find((s) => s.year === currentYear) ?? summaries[0]

  return (
    <Section profile={profile.data}>
      <AccountTabs active="earnings" isActor={isActor}>
        <div className="flex flex-col gap-4">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
          >
            <Stat
              label={`Paid in ${cur?.year ?? currentYear}`}
              value={fmtUsd(cur?.paid_cents ?? 0)}
              tone="green"
            />
            <Stat label="Outstanding" value={fmtUsd(cur?.outstanding_cents ?? 0)} tone="amber" />
            <Stat
              label="In-progress (estimate)"
              value={fmtUsd(cur?.in_progress_estimate_cents ?? 0)}
            />
          </div>

          <Card title="Projects">
            {earnings.isLoading ? (
              <div className="flex items-center gap-2 text-[var(--text-2)]">
                <Spinner /> Loading projects...
              </div>
            ) : projects.length === 0 ? (
              <EmptyState
                title="No earnings yet"
                description="They'll show up here as soon as a batch posts."
              />
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-3">
                {projects.map((p) => (
                  <li
                    key={p.project_id}
                    className="border-b border-[var(--border)] pb-3 last:border-b-0 last:pb-0"
                  >
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-medium">{p.project_name}</span>
                      <span className="text-[12px] text-[var(--text-2)]">
                        {p.organization_name}
                      </span>
                      <span className="text-[12px] text-[var(--text-2)] ml-auto">
                        {p.contract_summary}
                      </span>
                    </div>
                    <BatchTable batches={p.batches} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Annual summaries"
            actions={
              profile.data && summaries.length > 0 ? (
                <ExportButtons
                  exports={annual1099Exports(
                    profile.data.user_id,
                    summaries.map((s) => s.year),
                  )}
                />
              ) : null
            }
          >
            {summaries.length === 0 ? (
              <p className="text-[13px] text-[var(--text-2)] m-0">
                {t(copy.common.EMPTY.no_annual_data)}
              </p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col">
                {summaries.map((s) => {
                  const form = forms1099.find((f) => f.year === s.year)
                  return (
                    <li
                      key={s.year}
                      className="flex items-center gap-2 text-[13px] border-b border-[var(--border)] py-2 last:border-b-0"
                    >
                      <span className="font-medium tabular-nums">{s.year}</span>
                      <span className="text-[var(--text-2)]">
                        Paid {fmtUsd(s.paid_cents)} - Outstanding {fmtUsd(s.outstanding_cents)}
                      </span>
                      <span className="ml-auto">
                        {form ? (
                          <a
                            href={form.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--accent)] underline"
                          >
                            Stripe 1099 (PDF)
                          </a>
                        ) : (
                          <span className="text-[var(--text-3)]">Stripe 1099 not yet</span>
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>
        </div>
      </AccountTabs>
    </Section>
  )
}

function BatchTable({ batches }: { batches: readonly EarningsBatchRow[] }) {
  const t = useT()
  if (batches.length === 0) {
    return (
      <p className="text-[12px] text-[var(--text-2)] m-0 mt-1">
        {t(copy.common.EMPTY.no_batches_yet)}
      </p>
    )
  }
  return (
    <table className="w-full text-[12px] mt-2" role="table">
      <thead className="text-left text-[var(--text-2)]">
        <tr>
          <th className="font-medium py-1">Batch</th>
          <th className="font-medium py-1">Status</th>
          <th className="font-medium py-1">Amount</th>
          <th className="font-medium py-1">Paid on</th>
        </tr>
      </thead>
      <tbody>
        {batches.map((b) => (
          <tr key={b.batch_id} className="border-t border-[var(--border)]">
            <td className="py-1">{b.batch_label}</td>
            <td className="py-1">
              <Badge
                variant={
                  b.status === 'paid' ? 'green' : b.status === 'awaiting_payout' ? 'amber' : 'gray'
                }
              >
                {b.status.replace('_', ' ')}
              </Badge>
            </td>
            <td className="py-1 tabular-nums">{fmtUsd(b.amount_cents)}</td>
            <td className="py-1 text-[var(--text-2)]">
              {b.paid_at ? new Date(b.paid_at).toLocaleDateString() : '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'green' | 'amber'
}) {
  const color =
    tone === 'green'
      ? 'text-[var(--green)]'
      : tone === 'amber'
        ? 'text-[var(--amber)]'
        : 'text-[var(--text)]'
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{label}</div>
      <div className={`mt-0.5 text-[22px] font-semibold tabular-nums leading-tight ${color}`}>
        {value}
      </div>
    </Card>
  )
}

function annual1099Exports(actorId: string, years: number[]): ExportItem[] {
  const sorted = [...years].sort((a, b) => b - a)
  return sorted.map((year) => ({
    label: `OHEAR ${year} (PDF)`,
    format: 'pdf' as const,
    endpoint: '/api/v1/exports/1099',
    params: { actor_id: actorId, tax_year: year },
  }))
}

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function Section({
  children,
  profile,
}: { children: React.ReactNode; profile?: import('../../lib/api/account').AccountProfile }) {
  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile} />
      {children}
    </section>
  )
}
